/**
 * Shared TAS application and program instance state constants
 * Used for OpenSearch queries across dashboard, benefit history, and getting started routes
 */

/**
 * RecentApplication interface for OpenSearch results
 *
 * Payment fields are stored at root level in OpenSearch with long snake_case names.
 * Use `normalizePaymentFields()` after `OpenSearchResponseBuilder.getDocuments()` to normalize
 * payment fields to shorter, more convenient camelCase property names.
 */
export interface RecentApplication {
  id: string;
  recordType?: string; // 'instance' or 'application'
  tasApplicationIdKeyword?: string;
  tasApplicationStateKeyword?: string;
  tasProgramInstanceStateKeyword?: string;
  tasProgramInstanceId?: string;
  tasProgramInstanceIdKeyword?: string;
  tasProgramTemplateIdKeyword?: string;
  tasProgramTemplateFieldProgramNameKeyword?: string;
  tasProgramTemplateTypeKeyword?: string;
  tasApplicationFieldCoursesBeginDateDate?: string;
  tasApplicationFieldCoursesEndDateDate?: string;
  // Fields for displayName computation (Type 4 instances)
  tasApplicationFieldApplicationNameKeyword?: string;
  tasProgramInstanceFieldProgramSpecializationKeyword?: string;
  tasProgramInstanceFieldProgramMajorKeyword?: string;
  tasProgramInstanceFieldSchoolInstitutionNameKeyword?: string;
  applicationName?: string;
  programSpecialization?: string;
  programMajor?: string;
  // Payment fields (normalized to top level by normalizePaymentFields)
  paymentPaidAmount?: number;
  paymentPendingAmount?: number;
  paymentFinalApprovedTotalAmount?: number;
  paymentPreApprovedTotal?: number;
  paymentLastDate?: string;
  // Refund/repayment fields (for isInRepayment checks)
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
  // course cash fields
  courseCashUsedAmount?: number;
  courseCashReservedAmount?: number;
  courseCashRefundedAmount?: number;
  // Raw payment fields from OpenSearch (long camelCase names, before normalization)
  tasApplicationPaymentFields?: {
    tasApplicationPaymentPaidAmountInteger?: number;
    tasApplicationPaymentPendingAmountInteger?: number;
    tasApplicationPaymentFinalApprovedTotalAmountInteger?: number;
    tasApplicationPaymentPreApprovedTotalInteger?: number;
  };
  // Raw payment fields from OpenSearch (long camelCase names at root level)
  tasApplicationPaymentPaidAmountInteger?: number;
  tasApplicationPaymentPendingAmountInteger?: number;
  tasApplicationPaymentFinalApprovedTotalAmountInteger?: number;
  tasApplicationPaymentPreApprovedTotalInteger?: number;
  tasApplicationPaymentLastDateDate?: string;
  tasApplicationCourseCashReservedAmountInteger?: number;
  tasApplicationCourseCashUsedAmountInteger?: number;
  tasApplicationCourseCashRefundedAmountInteger?: number;
  tasApplicationPaymentRefundRequestedAmountInteger?: number;
  tasApplicationPaymentRefundPaidBackAmountInteger?: number;
  tasApplicationPaymentRefundForgivenAmountInteger?: number;
  tasApplicationPaymentRefundCancelledAmountInteger?: number;
  // TAS Application Field values (amounts in cents)
  tasApplicationFieldPreApprovedTotalInteger?: number;
  tasApplicationFieldFinalApprovedTotalInteger?: number;
  tasApplicationFieldCardPreApprovedTotalInteger?: number;
  tasApplicationFieldCardFinalApprovedTotalInteger?: number;
  // Dependent fields (from program instance)
  tasProgramInstanceDependentIdKeyword?: string;
  tasProgramInstanceDependentNameKeyword?: string;
  // Dates
  updatedAt?: string;
  createdAt?: string;
  documentCreatedAt?: string;
  tasApplicationCreatedAtDate?: string;
  tasProgramInstanceCreatedAtDate?: string;
}

/**
 * ProgramInstance interface for Type 4 instance groupings
 * Represents a Type 4 instance record with its child applications
 */
export interface ProgramInstance {
  instanceApplication: RecentApplication;
  childApplications: RecentApplication[];
}

/**
 * All TAS application states to include in OpenSearch queries
 * Used for Type 1, 2, 3, 4.2, and 4.3 programs
 */
export const ALL_APPLICATION_STATES = [
  // Draft states
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  // Active states
  'TAS.ApplicationState.ATTEND',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  // Closed states
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.FULFILLED',
  'TAS.ApplicationState.ABANDONED',
];

/**
 * Active application states (draft + in-progress)
 * Applications in these states should always be shown
 */
export const ACTIVE_APPLICATION_STATES = [
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.ATTEND',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ApplicationState.EVIDENCE_APPROVED',
];

/**
 * Inactive/closed application states
 * Applications in these states are only shown based on time window rules
 */
export const INACTIVE_APPLICATION_STATES = [
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.FULFILLED',
  'TAS.ApplicationState.ABANDONED',
];

/**
 * All TAS program instance states to include in OpenSearch queries
 * Used for Type 4.2/4.3 programs (instances with child applications)
 */
export const ALL_PROGRAM_INSTANCE_STATES = [
  'TAS.ProgramInstanceState.CREATED',
  'TAS.ProgramInstanceState.DRAFT',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
  'TAS.ProgramInstanceState.PROGRAM_APPROVED',
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED',
  'TAS.ProgramInstanceState.PROGRAM_REJECTED',
  'TAS.ProgramInstanceState.PRIMED',
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
  'TAS.ProgramInstanceState.COMPLETION_APPROVED',
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED',
  'TAS.ProgramInstanceState.COMPLETION_REJECTED',
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL',
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED',
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED',
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
  'TAS.ProgramInstanceState.COMPLETED',
  'TAS.ProgramInstanceState.WITHDRAWN',
  'TAS.ProgramInstanceState.ABANDONED',
];

/**
 * Active program instance states (draft + in-progress)
 * Instances in these states should always be shown
 */
export const ACTIVE_INSTANCE_STATES = [
  'TAS.ProgramInstanceState.CREATED',
  'TAS.ProgramInstanceState.DRAFT',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
  'TAS.ProgramInstanceState.PROGRAM_APPROVED',
  'TAS.ProgramInstanceState.PRIMED',
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
  'TAS.ProgramInstanceState.COMPLETION_APPROVED',
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL',
];

/**
 * Inactive/closed program instance states
 * Instances in these states are only shown based on time window rules
 */
export const INACTIVE_INSTANCE_STATES = [
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED',
  'TAS.ProgramInstanceState.PROGRAM_REJECTED',
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED',
  'TAS.ProgramInstanceState.COMPLETION_REJECTED',
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED',
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED',
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
  'TAS.ProgramInstanceState.COMPLETED',
  'TAS.ProgramInstanceState.WITHDRAWN',
  'TAS.ProgramInstanceState.ABANDONED',
];

/**
 * Check if an application state is considered "active"
 */
export function isActiveApplicationState(state: string | undefined): boolean {
  return ACTIVE_APPLICATION_STATES.includes(state || '');
}

/**
 * Check if a program instance state is considered "active"
 */
export function isActiveInstanceState(state: string | undefined): boolean {
  return ACTIVE_INSTANCE_STATES.includes(state || '');
}

/**
 * Check if an application is "in repayment" (owes money)
 * These should always be shown regardless of other rules
 *
 * Refund fields are indexed in OpenSearch and normalized via
 * `normalizePaymentFields()` so this works correctly for both
 * OpenSearch (RecentApplication) and Ember Data results.
 */
export function isInRepayment(app: {
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
}): boolean {
  return getOutstandingRepaymentBalance(app) > 0;
}

/**
 * Calculate outstanding repayment balance for an application
 *
 * Outstanding = refundRequested - (refundPaidBack + refundForgiven + refundCancelled)
 *
 * @param app - Application with payment refund fields
 * @returns Outstanding repayment amount in cents (0 if none or negative)
 */
export function getOutstandingRepaymentBalance(app: {
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
}): number {
  const refundRequested = app.paymentRefundRequestedAmount || 0;
  const refundPaidBack = app.paymentRefundPaidBackAmount || 0;
  const refundForgiven = app.paymentRefundForgivenAmount || 0;
  const refundCancelled = app.paymentRefundCancelledAmount || 0;

  const outstanding = refundRequested - (refundPaidBack + refundForgiven + refundCancelled);
  return Math.max(0, outstanding);
}

/**
 * Calculate net paid amount (paid minus outstanding repayment)
 *
 * @param app - Application with payment fields
 * @returns Net paid amount in cents (0 if negative)
 */
export function getNetPaidAmount(app: {
  paymentPaidAmount?: number;
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
  courseCashUsedAmount?: number;
}): number {
  const grossPaid = (app.paymentPaidAmount || 0) + (app.courseCashUsedAmount || 0);
  const outstanding = getOutstandingRepaymentBalance(app);
  return Math.max(0, grossPaid - outstanding);
}

/**
 * Document with payment fields at root level
 * Keys are camelCase after OpenSearchResponseBuilder.normalizeKeys().
 */
interface DocWithPaymentFields {
  // Top-level long camelCase field names (after normalizeKeys)
  tasApplicationPaymentPaidAmountInteger?: number;
  tasApplicationPaymentPendingAmountInteger?: number;
  tasApplicationPaymentFinalApprovedTotalAmountInteger?: number;
  tasApplicationPaymentPreApprovedTotalInteger?: number;
  tasApplicationPaymentLastDateDate?: string;
  tasApplicationCourseCashReservedAmountInteger?: number;
  tasApplicationCourseCashUsedAmountInteger?: number;
  tasApplicationCourseCashRefundedAmountInteger?: number;
  tasApplicationPaymentRefundRequestedAmountInteger?: number;
  tasApplicationPaymentRefundPaidBackAmountInteger?: number;
  tasApplicationPaymentRefundForgivenAmountInteger?: number;
  tasApplicationPaymentRefundCancelledAmountInteger?: number;
  // Already-normalized short names (may exist from aliases or prior processing)
  paymentPaidAmount?: number;
  paymentPendingAmount?: number;
  paymentFinalApprovedTotalAmount?: number;
  paymentPreApprovedTotal?: number;
  paymentLastDate?: string;
  courseCashReservedAmount?: number;
  courseCashUsedAmount?: number;
  courseCashRefundedAmount?: number;
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
}

/**
 * Normalize payment fields from long camelCase names to short names.
 *
 * Payment fields are stored at root level in OpenSearch with long camelCase names.
 * This function maps them to shorter, more convenient property names.
 *
 * @param docs - Documents from OpenSearchResponseBuilder.getDocuments()
 * @returns Documents with payment fields normalized to short names
 */
export function normalizePaymentFields<T>(docs: T[]): T[] {
  return docs.map((doc) => {
    const d = doc as DocWithPaymentFields;

    return {
      ...doc,
      // Map long camelCase names to short names (prefer existing short names if present)
      paymentPaidAmount: d.paymentPaidAmount ?? d.tasApplicationPaymentPaidAmountInteger,
      paymentPendingAmount: d.paymentPendingAmount ?? d.tasApplicationPaymentPendingAmountInteger,
      paymentFinalApprovedTotalAmount:
        d.paymentFinalApprovedTotalAmount ?? d.tasApplicationPaymentFinalApprovedTotalAmountInteger,
      paymentPreApprovedTotal:
        d.paymentPreApprovedTotal ?? d.tasApplicationPaymentPreApprovedTotalInteger,
      paymentLastDate: d.paymentLastDate ?? d.tasApplicationPaymentLastDateDate,
      courseCashReservedAmount:
        d.courseCashReservedAmount ?? d.tasApplicationCourseCashReservedAmountInteger,
      courseCashUsedAmount: d.courseCashUsedAmount ?? d.tasApplicationCourseCashUsedAmountInteger,
      courseCashRefundedAmount:
        d.courseCashRefundedAmount ?? d.tasApplicationCourseCashRefundedAmountInteger,
      paymentRefundRequestedAmount:
        d.paymentRefundRequestedAmount ?? d.tasApplicationPaymentRefundRequestedAmountInteger,
      paymentRefundPaidBackAmount:
        d.paymentRefundPaidBackAmount ?? d.tasApplicationPaymentRefundPaidBackAmountInteger,
      paymentRefundForgivenAmount:
        d.paymentRefundForgivenAmount ?? d.tasApplicationPaymentRefundForgivenAmountInteger,
      paymentRefundCancelledAmount:
        d.paymentRefundCancelledAmount ?? d.tasApplicationPaymentRefundCancelledAmountInteger,
    };
  });
}

/**
 * Build Type 4 instances from a map of instance ID to applications
 * Groups Type 4 instance records with their child applications
 *
 * @param instanceMap - Map of instance IDs to arrays of applications
 * @returns Array of Type 4 instances with their child applications, sorted by most recent activity
 */
export function buildType4Instances(
  instanceMap: Map<string, RecentApplication[]>
): ProgramInstance[] {
  const instances: ProgramInstance[] = [];

  for (const [, apps] of instanceMap) {
    // Sort apps by updatedAt to get most recent first
    const sortedApps = apps.sort(
      (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
    );

    // Separate instance record from application records using recordType
    const instanceRecord = sortedApps.find((app) => app.recordType === 'instance');
    const childApps = sortedApps.filter((app) => app.recordType !== 'instance');

    // Use instance record if it exists, otherwise use most recent child app
    const instanceApp = instanceRecord || sortedApps[0]!;

    instances.push({
      instanceApplication: instanceApp,
      childApplications: childApps,
    });
  }

  // Sort instances by most recent activity
  return instances.sort((a, b) => {
    const aDate = new Date(a.instanceApplication.updatedAt || 0).getTime();
    const bDate = new Date(b.instanceApplication.updatedAt || 0).getTime();
    return bDate - aDate;
  });
}
